import React, { useMemo, useState } from 'react';
import api from '../services/api';
import SEO from '../components/SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';

function Contact() {
  const { school, social } = useSiteSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await api.post('/contact', formData);
      console.log('✅ Message sent:', response.data);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('❌ Error sending message:', error);
      setError(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const phoneDigits = String(school.SCHOOL_PHONE || '').replace(/\D/g, '');
  const telHref = phoneDigits.length >= 10 ? `tel:+91${phoneDigits.slice(-10)}` : `tel:${school.SCHOOL_PHONE || ''}`;

  const socialLinks = useMemo(() => {
    return [
      { key: 'SOCIAL_FACEBOOK', fallback: '', label: 'Facebook' },
      { key: 'SOCIAL_INSTAGRAM', fallback: '', label: 'Instagram' },
      { key: 'SOCIAL_TWITTER', fallback: '', label: 'Twitter/X' },
    ]
      .map((l) => ({ ...l, url: social?.[l.key] || l.fallback }))
      .filter((l) => l.url && String(l.url).trim());
  }, [social]);

  const mapSrc =
    typeof school.GOOGLE_MAP_EMBED_URL === 'string' && /^https:\/\/www\.google\.com\/maps\/embed\?/i.test(school.GOOGLE_MAP_EMBED_URL.trim())
      ? school.GOOGLE_MAP_EMBED_URL.trim()
      : 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3496.0!2d76.9!3d29.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390dda2d86f1666d%3A0xc9ec1efd3f67e55e!2sVidya%20Vatika%20World%20School!5e0!3m2!1sen!2sin!4v1234567890';

  return (
    <>
      <SEO 
        title="Contact Us | Vidya Vatika World School Panipat"
        description="Get in touch with Vidya Vatika World School. Phone, email, and visit information. Quick response guaranteed."
        keywords="contact school Panipat, Vidya Vatika contact, school phone number, CBSE school Haryana"
        canonicalUrl="/contact"
        ogImage="/images/og-image.jpg"
      />

      <div className="pt-20 min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16 shadow-lg">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">Contact Us</h1>
            <p className="text-xl text-blue-100">We'd love to hear from you. Get in touch with us!</p>
          </div>
        </div>

        <div className="container mx-auto px-4 -mt-8 relative z-10">
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Left Column - Contact Form (2/3 width) */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <span className="text-2xl">✉️</span>
                  </div>
                  <h2 className="text-3xl font-bold text-blue-900">Send Us A Message</h2>
                </div>

                {submitted && (
                  <div className="mb-6 bg-green-50 border-2 border-green-200 text-green-800 px-6 py-4 rounded-xl flex items-center gap-3">
                    <span className="text-2xl">✅</span>
                    <span className="font-medium">Thank you! Your message has been sent successfully. We'll get back to you soon.</span>
                  </div>
                )}

                {error && (
                  <div className="mb-6 bg-red-50 border-2 border-red-200 text-red-800 px-6 py-4 rounded-xl flex items-center gap-3">
                    <span className="text-2xl">❌</span>
                    <span className="font-medium">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                      placeholder="What is this regarding?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent resize-none"
                      placeholder="Write your message here..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className={`w-full py-4 px-6 rounded-xl font-bold text-white text-lg transition-all shadow-lg hover:shadow-xl ${
                      submitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 transform hover:-translate-y-1'
                    }`}
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-3">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      '📨 Send Message'
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Right Column - Contact Info Cards (1/3 width) */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-xl p-6 text-white">
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <span className="text-3xl">📍</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Our Location</h3>
                    <p className="text-blue-50">
                      {school.SCHOOL_NAME}
                      <br />
                      {school.SCHOOL_ADDRESS}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-xl p-6 text-white">
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <span className="text-3xl">📞</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Phone Number</h3>
                    <a href={telHref} className="text-green-50 font-mono hover:underline">
                      {school.SCHOOL_PHONE}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <span className="text-3xl">✉️</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Email Address</h3>
                    <a href={`mailto:${school.SCHOOL_EMAIL_INFO}`} className="text-purple-50 hover:underline break-all">
                      {school.SCHOOL_EMAIL_INFO}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-xl p-6 text-white">
                <div className="flex items-start gap-4">
                  <div className="bg-white/20 p-3 rounded-xl">
                    <span className="text-3xl">⏰</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Office Hours</h3>
                    <p className="text-orange-50">Mon-Sat: 8:00 AM - 2:00 PM<br/>Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h3 className="font-bold text-blue-900 mb-4 text-lg">Follow Us On</h3>
                <div className="flex gap-3">
                  {socialLinks.length === 0 ? (
                    <p className="text-sm text-gray-500">Social links not configured.</p>
                  ) : (
                    socialLinks.map((l) => (
                      <a
                        key={l.key}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4 rounded-xl hover:from-blue-600 hover:to-blue-700 transition transform hover:scale-110 shadow-lg"
                      >
                        <span className="text-xs font-semibold">{l.label}</span>
                      </a>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Google Map - Full Width at Bottom */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-gray-100">
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white px-6 py-4">
              <h3 className="text-2xl font-bold flex items-center gap-3">
                <span className="text-3xl">🗺️</span>
                Find Us on Google Maps
              </h3>
            </div>
            <iframe
              src={mapSrc}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="School Location Map"
              className="w-full"
            ></iframe>
          </div>
        </div>
      </div>
    </>
  );
}

export default Contact;
